import { DOCUMENT } from '@angular/common';
import {
    Injector,
    Type,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@services/logger/abstract.logger';

import { AuthConfigAPI } from 'main/app-state/auth-config/auth-config.api';
import { CurrentUserAPI } from 'main/app-state/current-user/current-user.api';
import { Setting } from 'main/app-state/current-user/settings';
import { SettingsAPI } from 'main/app-state/current-user/settings/settings.api';
import { NonPublicallyNullableStateManager } from 'main/app-state/generics/state-manager';
import * as moment from 'moment';
import * as numbro from 'numbro';
import {
    BehaviorSubject,
    Observable,
    of,
} from 'rxjs';
import {
    map,
    pairwise,
    switchMap,
    tap,
} from 'rxjs/operators';

import { LanguageAPI } from './language.api';

const CSS_LANGUAGE_CLASS_PREFIX = 'lang-';

const EXTENDED_LOCALES = {
    'en': 'en-US',
    'ja': 'ja-JP',
    'de': 'de-DE',
    'fr': 'fr-FR',
    'ru': 'ru-RU',
    'zh': 'zh-CN',
    'es': 'es-ES',
    'ko': 'ko-KR',
    'tr': 'tr-TR',
    'pt': 'pt-PT',
} as Record<string, string>;

[
    require('numbro/dist/languages/ja-JP.min'),
    require('numbro/dist/languages/de-DE.min'),
    require('numbro/dist/languages/fr-FR.min'),
    require('numbro/dist/languages/ru-RU.min'),
    require('numbro/dist/languages/zh-CN.min'),
    require('numbro/dist/languages/es-ES.min'),
    require('numbro/dist/languages/es-MX.min'),
    require('numbro/dist/languages/ko-KR.min'),
    require('numbro/dist/languages/tr-TR.min'),
    require('numbro/dist/languages/pt-PT.min'),
    require('numbro/dist/languages/pt-BR.min'),
].forEach((locale) => numbro.registerLanguage(locale));

interface AppStateFacade {
    authConfig: AuthConfigAPI;
    user: CurrentUserAPI;
    settings: SettingsAPI;
}

/**
 * The current state is the language in which the app is displayed.
 * It's not its responsibility to send to server the user's currently set language (it isn't a
 * child of CurrentUserManager).
 */
export class LanguageManager extends NonPublicallyNullableStateManager<string> implements LanguageAPI {
    protected _state = new BehaviorSubject<string | null>(null);

    private _document: Document;
    private _translateService!: TranslateService;

    constructor(
        private _injector: Injector,
        private _appState: AppStateFacade,
        private _dateAdapter: DateAdapter<moment.Moment>,
    ) {
        super();

        this._document = this._injector.get(DOCUMENT);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('Language', this._injector.get<Logger>(Logger as Type<Logger>));

        this._state
            .pipe(
                pairwise(),
            )
            .subscribe(([prevLanguageCode, languageCode]) => {
                if (!languageCode) { return; }

                moment.locale(languageCode);
                numbro.setLanguage(
                    languageCode &&
                    EXTENDED_LOCALES[languageCode] || EXTENDED_LOCALES['en'],
                );
                this._dateAdapter.setLocale(languageCode);

                if (prevLanguageCode != null) {
                    this._document.body.classList.remove(`${CSS_LANGUAGE_CLASS_PREFIX}${prevLanguageCode}`);
                }

                this._document.body.classList.add(`${CSS_LANGUAGE_CLASS_PREFIX}${languageCode}`);
            });
    }

    /**
     * Should be called after either user's SettingsManager or AuthConfigManager are initialized.
     * Emits:
     * - true, when the initialization was successful
     * - false, in the unlikely case it initializes with a fallback value ('en'), because it
     *   was unable to determine the language from settings
     */
    public initialize(): Observable<boolean> {
        // Because TranslateService needs an instance of AppStateFacade, it can't be called before AppStateFacade's constructor
        // completes its execution (otherwise, AppStateFacade would be undefined).
        // Furthermore, if TranslateService would be injected in the constructor, then it would be instantiated twice.
        return of(true).pipe(
            map(() => {
                let language;
                let hasInitializedSuccessfully = true;
                this._translateService = this._injector.get<TranslateService>(TranslateService);
                this._translateService.setDefaultLang('en');
                moment.tz.setDefault('UTC');
                if (
                    this._appState.user.value &&
                    this._appState.settings.value
                ) {
                    language = this._appState.settings.get(Setting.currentCulture)!;
                } else if (this._appState.authConfig.value) {
                    language = this._appState.authConfig.value.currentCulture;
                } else {
                    language = 'en';
                    hasInitializedSuccessfully = false;
                }
                return { language, hasInitializedSuccessfully };
            }),
            switchMap(({ language, hasInitializedSuccessfully }) => this.set(language).pipe(
                map(() => hasInitializedSuccessfully),
            )),
        );
    }

    public set(newLanguage: string) {
        return this._translateService
            .use(newLanguage)
            .pipe(
                tap(() => this._state.next(newLanguage)),
                map(() => true),
            );
    }
}

